<template>
    <v-dialog v-model="show" width="750" persistent no-click-animation>
        <div class="ManagePermissionEditor">
            <!--Dialog header-->
            <div class="dialog-header">
                <div>{{ $f.tc('managePermission') }}: {{
                        campaign ? $utils.limitStringLength(campaign.title, 45) : ''
                    }}
                </div>
                <v-btn small icon @click="close">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </div>
            <!--Dialog content-->
            <div>
                <div class="permission-creator">
                    <v-form v-model="formValid" @submit="submit" ref="form" class="centerY" style="gap: 8px">
                        <v-text-field
                            v-model="newUserPermission.email"
                            :placeholder="$f.tc('email')"
                            :rules="formRules.email"
                            class="input-custom"
                        ></v-text-field>
                        <div style="width: 230px">
                            <v-select
                                outlined
                                v-model="newUserPermission.permission"
                                :placeholder="$f.tc('permission')"
                                :rules="formRules.required"
                                :items="permissionList"
                                class="input-custom"
                            ></v-select>
                        </div>
                        <div class="btn-outline" @click="createUserPermission">
                            <img
                                src="/img/add-blue.svg"
                                alt="icon-assign-permission"
                                style="transform: scale(1.2); margin-right: 8px"
                            >
                            {{ $f.tc('assign') }}
                        </div>
                    </v-form>
                </div>
                <div class="editor-actions">
                    <div class="btn-custom" @click="downloadTemplateUserPermission">
                        <img
                            src="/img/download-white.svg"
                            alt="icon-download"
                            style="transform: scale(1.05); margin-right: 8px"
                        >
                        {{ $f.tc('downloadTemplateExcel') }}
                    </div>
                    <div class="btn-custom" @click="importPermission">
                        <img
                            src="/img/upload-white.svg"
                            alt="icon-import-excel"
                            style="transform: scale(1.05); margin-right: 8px"
                        >
                        {{ $f.tc('importExcel') }}
                    </div>
                    <input type="file" hidden ref="inputPermission"
                           accept=".xlsx, .xls, .csv"
                           @change="handleFile">
                </div>

                <!--List user permission-->
                <div class="scrollbar-thin table-permission">
                    <SimpleDataTable
                        :headers="headers"
                        :items="dataTable"
                        :hide-pager="true"
                    >
                        <template v-slot:action="props">
                            <v-btn icon small @click="userToDelete = props.item ; showConfirmOverlay = true">
                                <img src="/img/delete-blue.svg" alt="icon-delete"
                                     style="transform: scale(1.1); padding-left: 1px">
                            </v-btn>
                        </template>
                    </SimpleDataTable>
                </div>
                <confirm-overlay
                    :show="showConfirmOverlay"
                    title="Remove User"
                    :text="userToDelete.email"
                    @disagree="showConfirmOverlay = false; userToDelete = false"
                    @agree="showConfirmOverlay = false; deleteUserPermissions(userToDelete.id)"></confirm-overlay>
            </div>
        </div>
    </v-dialog>
</template>

<script>
import SimpleDataTable from '@/components/SimpleDataTable'
import ConfirmOverlay from '@/components/ConfirmOverlay'
import CampaignService from '@/services/campaign.service'
import FormMixin from "@/modules/fptai/mixins/FormMixin";

const userPermissionDefault = {
    email: '',
    permission: ''
}

export default {
    name: 'ManagePermissionEditor',
    components: {
        SimpleDataTable,
        ConfirmOverlay
    },
    mixins: [FormMixin],
    props: {
        onSaved: Function
    },
    data() {
        return {
            formValid: false,
            show: false,
            campaign: null,
            dataTable: [],
            showConfirmOverlay: false,
            userToDelete: false,
            newUserPermission: this.$utils.cloneObject(userPermissionDefault),
            permissionList: [],
        }
    },
    computed: {
        headers() {
            return [
                {
                    text: this.$filters.capitalize(this.$t('email')),
                    value: 'email',
                    sortable: false
                },
                {
                    text: this.$filters.capitalize(this.$t('permission')),
                    value: 'permission',
                    sortable: true,
                    class: 'text-capitalize'
                },
                {
                    text: this.$filters.capitalize(this.$t('action')),
                    value: 'action',
                    sortable: false,
                    type: 'custom',
                    id: 'action'
                }
            ]
        }
    },
    methods: {
        open(campaign) {
            this.loadPermissionRoles().then(() => {
                this.show = true
                this.campaign = campaign
                this.clearForm()
            })
        },
        close() {
            this.show = false
        },
        loadPermissionRoles() {
            this.$appService.showLoading()
            return CampaignService.getCampaignRoles().then(
                result => {
                    this.permissionList = result.data.map(item => {
                        return {
                            text: item.name,
                            value: item.permission
                        }
                    })
                },
                this.$appService.handleRequestError('can not load campaign roles')
            ).finally(this.$appService.hideLoading)
        },
        loadUserPermissions() {
            CampaignService.getUserPermissions(this.campaign).then(
                result => {
                    this.dataTable = result.data
                }
            )
        },
        deleteUserPermissions(userId) {
            const i18n = {
                action: this.$t('delete'),
                item: this.$t('permission')
            }
            this.$appService.showLoading()
            CampaignService.deleteUserPermissions(this.campaign, userId).then(() => {
                this.$appService.toastSuccess(this.$t('actionSuccessful', i18n))
                this.userToDelete = false
                this.loadUserPermissions()
            }).finally(this.$appService.hideLoading)
        },
        importPermission() {
            this.$refs.inputPermission.click()
        },
        handleFile(event) {
            this.file = event.target.files[0]
            let extFile = this.file.name.split('.').pop()
            if (this.file.size <= 1024 * 1024 && ['xlsx', 'csv', 'xls'].includes(extFile)) {
                let formData = new FormData()
                formData.append('file', this.file)
                this.$appService.showLoading()
                this.$http.post(
                    `/campaigns/${this.campaign.id}/import-campaign-permission/`,
                    formData,
                    {headers: {'Content-Type': 'multipart/form-data'}}
                ).then(
                    res => {
                        const i18n = {
                            action: this.$t('save'),
                            item: this.$t('permission')
                        }
                        this.$appService.toastSuccess(this.$f.tc('actionSuccessful', i18n) + ': ' + res.data.import_success)
                        this.$appService.toastError(this.$f.tc('actionFailed', i18n) + ': ' + res.data.import_fail.count)
                        this.loadUserPermissions()
                    }
                ).catch(
                    err => {
                        this.$appService.toastError(err)
                    }
                ).finally(() => {
                    this.$appService.hideLoading()
                    this.file = null
                    this.$refs.inputPermission.value = null
                })

            } else {
                this.$appService.toastError('invalid file')
            }
        },
        downloadTemplateUserPermission() {
            CampaignService.getCampaignPermissionTemplate(this.campaign).then(res => {
                this.$utils.saveFile(res.data, this.$filters.replaceSpecialCharacters(this.$utils.nonAccentVietnamese(this.campaign.title + ' - campaign permission template')), '.xlsx')
            })
        },
        createUserPermission() {
            const i18n = {
                action: this.$t('assign'),
                item: this.$t('permission')
            }

            CampaignService.saveUserPermissions(this.campaign, this.newUserPermission).then(() => {
                    this.$appService.toastSuccess(this.$f.tc('actionSuccessful', i18n))
                    if (this.onSaved) {
                        this.onSaved()
                    }

                    this.clearForm()
                    this.loadUserPermissions()
                }
            )
        }
    },
    watch: {
        campaign: {
            immediate: true,
            handler: function () {
                if (this.campaign) {
                    this.loadUserPermissions()
                }
            }
        }
    }
}
</script>

<style scoped lang="scss">
.ManagePermissionEditor {
    background: #FFFFFF;
    border-radius: 5px !important;
    overflow: hidden;

    .permission-creator {
        padding: 12px;
        border-bottom: 1px solid #D9D9D9;
    }

    .editor-actions {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        padding: 12px 12px 0 12px;
    }

    .table-permission {
        margin: 12px;
        max-height: 600px;
        overflow-y: auto;
    }

    .btn-custom {
        margin-left: 8px;
    }
}

.input-custom {
    ::v-deep {
        .v-input__slot {
            height: 32px;
            background: #FFFFFF;
            border-radius: 5px;
            border: 1px solid #D9D9D9;
            outline: none;
            font-size: 14px;
            padding: 6px 12px;
            margin: 0;
            display: flex;
            align-items: center;
            position: relative;

            &:before, &:after {
                border: none !important;
            }
        }
    }
}

::v-deep {
    .v-text-field {
        margin-top: 0 !important;
        padding-top: 0 !important;
        position: relative;
    }

    .v-text-field__details {
        position: absolute;
        right: 12px;
        top: 9px
    }

    .v-select {
        .v-input__slot {
            padding: 6px 3px 6px 12px !important;
        }

        .v-text-field__details {
            right: 30px !important;
        }
    }

    .v-table__overflow {
        border: 1px solid #96add0;

        thead tr:not(.v-datatable__progress) {
            height: 48px !important;
        }

        thead th {
            text-transform: capitalize !important;
            color: #12274F !important;
            background: #E4EDFA;
        }
    }
}
</style>
